import { useState, useEffect } from 'react'
import { devicesApi, type Device } from '../api/devices'
import { filesApi } from '@/features/files/api/files'
import { useAuthStore } from '@/features/auth/stores/authStore'
import { getErrorMessage } from '@/shared/api/client'
import { toast } from '@/shared/stores/toastStore'
import type { ClientConfig } from '@/shared/api/generated'

function detectBrowser(): string {
  const ua = navigator.userAgent
  if (ua.includes('Firefox')) return 'Firefox'
  if (ua.includes('Edg')) return 'Edge'
  if (ua.includes('Chrome')) return 'Chrome'
  if (ua.includes('Safari')) return 'Safari'
  return 'Unknown'
}

function detectOS(): string {
  const ua = navigator.userAgent
  if (ua.includes('Windows')) return 'Windows'
  if (ua.includes('Mac')) return 'macOS'
  if (ua.includes('Linux')) return 'Linux'
  return 'Unknown'
}

function downloadBlob(content: string, filename: string) {
  const blob = new Blob([content], { type: 'application/octet-stream' })
  const a = document.createElement('a')
  a.href = URL.createObjectURL(blob)
  a.download = filename
  a.click()
  URL.revokeObjectURL(a.href)
}

export function generateWebDAVMap(username: string, password: string, domain: string, siteName = 'Cirrus'): string {
  return `@echo off\r\nREM ${siteName} WebDAV Mount Script\r\n\r\nREM Check WebClient service is available\r\nsc query WebClient >nul 2>&1\r\nif %errorlevel% neq 0 (\r\n    echo ERROR: WebClient service is not installed.\r\n    echo WebDAV is not available on Windows Server editions.\r\n    echo Use SSHFS instead, or see the Drive page for setup instructions.\r\n    pause\r\n    exit /b 1\r\n)\r\nnet start WebClient >nul 2>&1\r\n\r\necho Mounting WebDAV drive as Z:...\r\nnet use Z: "https://${domain}/dav" /user:${username} "${password}" /persistent:no\r\n\r\nif %errorlevel% equ 0 (\r\n    echo.\r\n    echo SUCCESS! Drive Z: is now available.\r\n    echo Opening File Explorer...\r\n    explorer Z:\\\r\n) else (\r\n    echo.\r\n    echo FAILED to mount drive. See the Drive page for troubleshooting.\r\n    pause\r\n)\r\n`
}

export function generateWebDAVUnmap(siteName = 'Cirrus'): string {
  return `@echo off\r\nREM ${siteName} WebDAV Unmount Script\r\n\r\necho Unmounting drive Z:...\r\nnet use Z: /delete /y\r\n\r\nif %errorlevel% equ 0 (\r\n    echo Drive Z: unmounted successfully.\r\n) else (\r\n    echo Failed to unmount drive Z:.\r\n)\r\npause\r\n`
}

export function generateSSHFSMap(username: string, password: string, domain: string, sftpPort: number, siteName = 'Cirrus'): string {
  return `@echo off\r\nREM ${siteName} SSHFS Mount Script\r\nREM Requires: WinFsp (https://winfsp.dev/rel/) + SSHFS-Win (https://github.com/winfsp/sshfs-win/releases)\r\n\r\nREM Check SSHFS-Win is installed\r\nwhere sshfs-win.exe >nul 2>&1\r\nif %errorlevel% neq 0 (\r\n    echo ERROR: SSHFS-Win is not installed.\r\n    echo.\r\n    echo Install in this order:\r\n    echo 1. WinFsp:    https://winfsp.dev/rel/\r\n    echo 2. SSHFS-Win: https://github.com/winfsp/sshfs-win/releases\r\n    echo 3. Restart your computer\r\n    echo 4. Run this script again\r\n    pause\r\n    exit /b 1\r\n)\r\n\r\necho Mounting SSHFS drive as Z:...\r\nnet use Z: "\\\\sshfs\\${username}@${domain}!${sftpPort}" /user:${username} "${password}" /persistent:no\r\n\r\nif %errorlevel% equ 0 (\r\n    echo.\r\n    echo SUCCESS! Drive Z: is now available.\r\n    echo Opening File Explorer...\r\n    explorer Z:\\\r\n) else (\r\n    echo.\r\n    echo FAILED to mount drive. See the Drive page for troubleshooting.\r\n    pause\r\n)\r\n`
}

export function generateSSHFSUnmap(siteName = 'Cirrus'): string {
  return `@echo off\r\nREM ${siteName} SSHFS Unmount Script\r\n\r\necho Unmounting drive Z:...\r\nnet use Z: /delete /y\r\n\r\nif %errorlevel% equ 0 (\r\n    echo Drive Z: unmounted successfully.\r\n) else (\r\n    echo Failed to unmount drive Z:.\r\n)\r\npause\r\n`
}

export function generateMacWebDAVMap(username: string, password: string, domain: string): string {
  return `#!/bin/bash\n# Mount WebDAV\nMOUNT="$HOME/Cirrus"\nmkdir -p "$MOUNT"\nif mount | grep -q "$MOUNT"; then echo "Already mounted"; open "$MOUNT"; exit 0; fi\nmount_webdav -s -S -i "https://${username}:${password}@${domain}/dav" "$MOUNT" 2>/dev/null \\\n  && { echo "Mounted."; open "$MOUNT"; } \\\n  || osascript -e 'tell application "Finder" to open location "https://${domain}/dav"'\n`
}

export function generateMacSSHFSMap(username: string, password: string, domain: string, sftpPort: number): string {
  return `#!/bin/bash\n# Mount SSHFS (requires: brew install macfuse sshfs)\nMOUNT="$HOME/Cirrus"\nmkdir -p "$MOUNT"\nif ! command -v sshfs &>/dev/null; then echo "Install sshfs: brew install macfuse sshfs"; exit 1; fi\nif mount | grep -q "$MOUNT"; then echo "Already mounted"; open "$MOUNT"; exit 0; fi\necho "${password}" | sshfs "${username}@${domain}:/" "$MOUNT" \\\n  -o port=${sftpPort} -o password_stdin -o volname=Cirrus \\\n  -o defer_permissions -o noappledouble -o noapplexattr \\\n  && { echo "Mounted."; open "$MOUNT"; } \\\n  || { echo "Failed. Check credentials and port ${sftpPort}."; exit 1; }\n`
}

export function generateMacUnmount(): string {
  return `#!/bin/bash\n# Unmount Cirrus\nMOUNT="$HOME/Cirrus"\nif ! mount | grep -q "$MOUNT"; then echo "Not mounted."; exit 0; fi\numount "$MOUNT" 2>/dev/null || diskutil unmount "$MOUNT" 2>/dev/null || { echo "Failed. Try: sudo umount -f $MOUNT"; exit 1; }\necho "Unmounted."\n`
}

function PrerequisitesPanel({ os, domain }: { os: string; domain: string }) {
  const [tab, setTab] = useState(os === 'macOS' ? 'macos' : os === 'Linux' ? 'linux' : 'windows')
  return (
    <div className="prereqs">
      <div className="tab-bar" role="tablist">
        {(['windows', 'macos', 'linux'] as const).map(t => (
          <button key={t} role="tab" aria-selected={tab === t} className={`tab${tab === t ? ' active' : ''}`}
            onClick={() => setTab(t)}>{t === 'macos' ? 'macOS' : t.charAt(0).toUpperCase() + t.slice(1)}</button>
        ))}
      </div>
      {tab === 'windows' && (
        <div role="tabpanel" className="prereqs-panel">
          <div className="prereqs-section">
            <h3>Option 1: WebDAV</h3>
            <p>WebDAV is built into Windows 10 and Windows 11 (Home, Pro, Enterprise). It does <strong>not</strong> work on Windows Server.</p>
            <p>Before using WebDAV, you need to start a Windows service:</p>
            <ol>
              <li>Press <strong>Win + R</strong>, type <code>services.msc</code>, and press Enter</li>
              <li>Scroll down and find <strong>WebClient</strong></li>
              <li>Right-click it → click <strong>Start</strong></li>
              <li>Right-click it again → <strong>Properties</strong> → set "Startup type" to <strong>Automatic</strong></li>
            </ol>
            <p className="muted">If you don't see "WebClient" in the list, your Windows edition doesn't support it. Use SSHFS instead (see below).</p>
            <p>Once WebClient is running, you can connect using a mount script from the "Mount Scripts" section (it has your current password built in), or manually through File Explorer:</p>
            <ol>
              <li>Open <strong>File Explorer</strong> (Win + E)</li>
              <li>Right-click <strong>This PC</strong> → <strong>Map network drive…</strong></li>
              <li>Choose a drive letter (e.g., Z:)</li>
              <li>In the "Folder" field, enter: <code>{'\\\\'}{ domain }{'\\'}dav</code></li>
              <li>Check <strong>"Connect using different credentials"</strong></li>
              <li>Click <strong>Finish</strong>, then enter your username and the temporary password from the "Registered Devices" section above</li>
            </ol>
            <p className="muted">The password changes every time you log in to the web interface and stops working when you log out. You can also use the mount script below instead.</p>
          </div>
          <div className="prereqs-section">
            <h3>Option 2: SSHFS (recommended)</h3>
            <p>SSHFS works on all Windows editions. You need to install two free programs:</p>
            <ol>
              <li>
                Download and install <a href="https://winfsp.dev/rel/" target="_blank" rel="noopener noreferrer"><strong>WinFsp</strong></a>
                <ul><li>Run the installer, click "Next" through all steps</li></ul>
              </li>
              <li>
                Download and install <a href="https://github.com/winfsp/sshfs-win/releases" target="_blank" rel="noopener noreferrer"><strong>SSHFS-Win</strong></a>
                <ul><li>On the releases page, download the <code>.msi</code> file and run it</li></ul>
              </li>
              <li><strong>Restart your computer</strong> after installing both</li>
            </ol>
          </div>
        </div>
      )}
      {tab === 'macos' && (
        <div role="tabpanel" className="prereqs-panel">
          <div className="prereqs-section">
            <h3>Option 1: WebDAV</h3>
            <p>WebDAV works out of the box on macOS — no installation needed.</p>
            <p>To connect: open <strong>Finder</strong> → menu <strong>Go</strong> → <strong>Connect to Server…</strong> and enter <code>https://{domain}/dav</code>. When prompted, use your username and the temporary password shown in the "Registered Devices" section above. This password changes every time you log in to the web interface and stops working when you log out.</p>
          </div>
          <div className="prereqs-section">
            <h3>Option 2: SSHFS</h3>
            <p>SSHFS requires installing two free programs:</p>
            <ol>
              <li>
                Download and install <a href="https://osxfuse.github.io/" target="_blank" rel="noopener noreferrer"><strong>macFUSE</strong></a>
                <ul><li>Open the downloaded <code>.dmg</code> file and follow the installer</li>
                <li>You may need to allow it in <strong>System Settings → Privacy &amp; Security</strong></li></ul>
              </li>
              <li>
                Install <strong>sshfs</strong> using Homebrew (a free package manager for macOS):
                <ul>
                  <li>If you don't have Homebrew yet, open <strong>Terminal</strong> and paste:<br/>
                    <code>/bin/bash -c "$(curl -fsSL https://raw.githubusercontent.com/Homebrew/install/HEAD/install.sh)"</code></li>
                  <li>Then run: <code>brew install sshfs</code></li>
                </ul>
              </li>
              <li><strong>Restart your computer</strong> after installing macFUSE</li>
            </ol>
          </div>
        </div>
      )}
      {tab === 'linux' && (
        <div role="tabpanel" className="prereqs-panel">
          <div className="prereqs-section">
            <h3>Option 1: WebDAV</h3>
            <p>Most Linux file managers support WebDAV out of the box (GNOME Files, Dolphin, Nautilus).</p>
            <p>To connect: open your file manager → look for "Other Locations" or "Connect to Server" and enter <code>davs://{domain}/dav</code>. When prompted, use your username and the temporary password from the "Registered Devices" section above. The password changes on every web login and is only valid until you log out.</p>
            <p>For command-line mounting, install <code>davfs2</code>:</p>
            <ul>
              <li>Debian / Ubuntu: <code>sudo apt install davfs2</code></li>
              <li>Fedora / RHEL: <code>sudo dnf install davfs2</code></li>
            </ul>
          </div>
          <div className="prereqs-section">
            <h3>Option 2: SSHFS</h3>
            <p>Install the <code>sshfs</code> package:</p>
            <ul>
              <li>Debian / Ubuntu: <code>sudo apt install sshfs</code></li>
              <li>Fedora / RHEL: <code>sudo dnf install fuse-sshfs</code></li>
            </ul>
          </div>
        </div>
      )}
    </div>
  )
}

export function DriveSection() {
  const [devices, setDevices] = useState<Device[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [newPassword, setNewPassword] = useState('')
  const [config, setConfig] = useState<ClientConfig | null>(null)
  const [dlWebdav, setDlWebdav] = useState(true)
  const [dlSshfs, setDlSshfs] = useState(true)
  const [downloadMsg, setDownloadMsg] = useState('')
  const username = useAuthStore(s => s.user?.username || '')

  useEffect(() => {
    loadDevices()
    filesApi.getConfig().then(setConfig).catch(err => toast.error(getErrorMessage(err)))
  }, [])

  const loadDevices = async () => {
    setLoading(true)
    setError('')
    try {
      setDevices(await devicesApi.list() || [])
    } catch (err) {
      setError(getErrorMessage(err))
    } finally {
      setLoading(false)
    }
  }

  const handleRegister = async () => {
    try {
      const result = await devicesApi.register(detectBrowser(), detectOS(), navigator.userAgent)
      setNewPassword(result.webdav_password)
      await loadDevices()
    } catch (err) {
      setError(getErrorMessage(err))
    }
  }

  const handleGeneratePassword = async () => {
    try {
      const password = await devicesApi.generatePassword()
      setNewPassword(password)
      await loadDevices()
    } catch (err) {
      setError(getErrorMessage(err))
    }
  }

  const handleRegeneratePassword = async () => {
    if (!window.confirm('Regenerate password? Old password will stop working.')) return
    await handleGeneratePassword()
  }

  const handleRevoke = async (deviceId: string) => {
    if (!window.confirm('Unregister this device?')) return
    try {
      await devicesApi.revoke(deviceId)
      await loadDevices()
    } catch (err) {
      setError(getErrorMessage(err))
    }
  }

  const handleDownloadScripts = () => {
    const domain = config?.server_domain || window.location.hostname
    const sftpPort = config?.sftp_port || 222
    const pw = currentPassword
    if (!pw) return

    const sn = config?.site_name || 'Cirrus'
    const isMac = detectOS() === 'macOS'
    const isUnix = isMac || detectOS() === 'Linux'

    let count = 0
    if (dlWebdav) {
      if (isUnix) {
        downloadBlob(generateMacWebDAVMap(username, pw, domain), 'mount-webdav.command')
      } else {
        downloadBlob(generateWebDAVMap(username, pw, domain, sn), 'drive-Z-map-webdav.bat')
        downloadBlob(generateWebDAVUnmap(sn), 'drive-Z-unmap-webdav.bat')
      }
      count += isUnix ? 1 : 2
    }
    if (dlSshfs) {
      if (isUnix) {
        downloadBlob(generateMacSSHFSMap(username, pw, domain, sftpPort), 'mount-sshfs.command')
      } else {
        downloadBlob(generateSSHFSMap(username, pw, domain, sftpPort, sn), 'drive-Z-map-sshfs.bat')
        downloadBlob(generateSSHFSUnmap(sn), 'drive-Z-unmap-sshfs.bat')
      }
      count += isUnix ? 1 : 2
    }
    if (isUnix && (dlWebdav || dlSshfs)) {
      downloadBlob(generateMacUnmount(), 'unmount.command')
      count += 1
    }
    setDownloadMsg(`✓ ${count} script${count > 1 ? 's' : ''} downloaded`)
    setTimeout(() => setDownloadMsg(''), 4000)
  }

  const currentDevice = devices.find(d => d.is_current)
  const otherDevices = devices.filter(d => !d.is_current)
  const currentPassword = currentDevice?.password || newPassword

  if (loading) return (
    <div className="page">
      <div className="page-header">
        <h1>Network Drive</h1>
        <p className="muted">Manage WebDAV and SSHFS device access</p>
      </div>
      <p className="muted">Loading...</p>
    </div>
  )

  return (
    <div className="page">
      <div className="page-header">
        <h1>Network Drive</h1>
        <p className="muted">Manage WebDAV and SSHFS device access</p>
      </div>

      {error && <div className="alert error">{error}</div>}

      <div className="card">
        <div className="card-header">
          <h2>Registered Devices</h2>
          <span className="muted small">{devices.length}/2 devices</span>
        </div>

        <table>
          <thead>
            <tr>
              <th>Device</th>
              <th>Name</th>
              <th>Status</th>
              <th className="right">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>This device</strong></td>
              <td>{currentDevice?.device_name || `${detectBrowser()} on ${detectOS()}`}</td>
              <td>
                {currentDevice ? (
                  currentPassword ? (
                    <span className="password-display">
                      Password: <code>{showPassword ? currentPassword : '••••••••'}</code>
                      <button className="link" onClick={() => setShowPassword(!showPassword)}>
                        {showPassword ? 'Hide' : 'Show'}
                      </button>
                      <button className="link" onClick={() => { navigator.clipboard.writeText(currentPassword); }}>
                        Copy
                      </button>
                    </span>
                  ) : 'No password'
                ) : 'Not registered'}
              </td>
              <td className="right">
                {currentDevice ? (
                  <button className="link" onClick={handleRegeneratePassword}>
                    {currentPassword ? 'Regenerate' : 'Generate Password'}
                  </button>
                ) : (
                  <button className="btn-primary" onClick={handleRegister} disabled={devices.length >= 2}>
                    Register
                  </button>
                )}
              </td>
            </tr>

            {otherDevices.map(device => (
              <tr key={device.device_id}>
                <td>Other device</td>
                <td>{device.device_name}</td>
                <td>Last used: {device.last_used || 'Never'}</td>
                <td className="right">
                  <button className="link danger" onClick={() => handleRevoke(device.device_id)}>Unregister</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {currentPassword && (
        <div className="drive-panels">
          <div className="card">
            <div className="card-header">
              <h2>Prerequisites</h2>
              <span className="muted small">Install before using mount scripts</span>
            </div>
            <div className="card-body">
              <PrerequisitesPanel os={detectOS()} domain={config?.server_domain || window.location.hostname} />
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <h2>Mount Scripts</h2>
              <span className="muted small">Download ready-to-run scripts with your password pre-filled ({detectOS() === 'Windows' ? '.bat' : '.command'})</span>
            </div>
            <div className="card-body">
              <p className="muted">
                Scripts contain your current password. {detectOS() === 'Windows'
                  ? <>After downloading, run the <strong>latest</strong> file from your Downloads folder — it may appear as <code>drive-Z-map-... (2).bat</code> if you've downloaded before.</>
                  : <>After downloading, run <code>chmod +x *.command &amp;&amp; ./mount-*.command</code> from your Downloads folder.</>}
              </p>
              <label className="checkbox-label">
                <input type="checkbox" checked={dlWebdav} onChange={e => setDlWebdav(e.target.checked)} />
                WebDAV scripts (map + unmap)
              </label>
              <label className="checkbox-label">
                <input type="checkbox" checked={dlSshfs} onChange={e => setDlSshfs(e.target.checked)} />
                SSHFS scripts (map + unmap)
              </label>
              <button className="btn-success" onClick={handleDownloadScripts} disabled={!dlWebdav && !dlSshfs}>
                Download Scripts
              </button>
              {downloadMsg && <span className="text-success">{downloadMsg}</span>}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
